/**
 * Where a work order sits in the review → approval → billing chain (#496).
 *
 * DERIVED, never stored. The three states below are read off two timestamps
 * (`review_approved_at`, `pm_approved`) plus `status`, because `status` itself has to stay
 * 'completed' the whole way through — the billing queue, `cost-summary`'s isBillable and
 * the maintenance edit-lock all key on that literal, and a fourth status value would drop
 * these rows out of all three. See migration 250.
 *
 * ONE definition, imported by both sides. The amber badge on the work-order detail, the
 * PM dashboard card, accounting's queue tabs and the escalation digest must agree about
 * what "pending approval" means; the inbox digest's scar is that a sweep which disagrees
 * with the screen it links to is worse than no sweep.
 */

import { WorkOrderStatus } from '@/types/models';

export enum WorkOrderApprovalState {
	/** Not finished, or cancelled — approval doesn't apply yet. */
	NotApplicable = 'not_applicable',
	/** Maintenance finished; accounting hasn't written the customer-facing description. */
	NeedsReview = 'needs_review',
	/** Accounting is done. Waiting on the property manager. */
	PendingApproval = 'pending_approval',
	/** The PM approved it. Eligible for billing. */
	Approved = 'approved',
}

/** The subset of a work order this derivation needs, so callers can pass rows or models. */
export interface ApprovalStateInput {
	status?: string | null;
	reviewApprovedAt?: number | string | null;
	pmApproved?: number | string | null;
}

/**
 * Truthy-but-not-empty check for a timestamp that may arrive as ms, an ISO string, or null.
 *
 * The API returns `pmApproved` as an ISO string (api/work-orders.ts) while D1 rows and the
 * dashboard's own optimistic updates carry raw ms, so a bare `!!` on one shape and a
 * `> 0` on the other would disagree. `0` is not a real approval instant in this app —
 * nothing is approved at the epoch — so treating it as unset is safe and catches a
 * `Number(null)` that slipped through somewhere upstream.
 */
const isSet = (v: number | string | null | undefined): boolean => {
	if (v == null || v === '') return false;
	if (typeof v === 'number') return Number.isFinite(v) && v > 0;
	return true;
};

export function deriveApprovalState(wo: ApprovalStateInput): WorkOrderApprovalState {
	if (wo.status !== WorkOrderStatus.Completed) return WorkOrderApprovalState.NotApplicable;
	if (isSet(wo.pmApproved)) return WorkOrderApprovalState.Approved;
	if (isSet(wo.reviewApprovedAt)) return WorkOrderApprovalState.PendingApproval;
	return WorkOrderApprovalState.NeedsReview;
}

/**
 * Did a property manager send this back, and is it still waiting on accounting? (#503)
 *
 * A sub-state of NEEDS REVIEW, not a fifth state: the row is in accounting's queue either
 * way, and every consumer of `deriveApprovalState` must keep treating it as such. What
 * changes is urgency — a PM is already waiting, and the reason they gave is the whole
 * brief for the rewrite — so the detail badge and the To Do list surface it in red.
 *
 * Gated on NEEDS REVIEW rather than on the timestamp alone. `ready-for-approval` clears
 * `review_returned_at` when accounting re-releases the work order, but a row that reached
 * the client before that write landed would otherwise flash "sent back" on a PM's approval
 * queue, where it is theirs to approve rather than accounting's to redo.
 */
export function isReturnedForReview(
	wo: ApprovalStateInput & { reviewReturnedAt?: number | string | null },
): boolean {
	return deriveApprovalState(wo) === WorkOrderApprovalState.NeedsReview && isSet(wo.reviewReturnedAt);
}

/** The red the sent-back marker uses everywhere — matches the overdue red, on purpose. */
export const RETURNED_FOR_REVIEW_COLOR = '#c62828';
export const RETURNED_FOR_REVIEW_LABEL = 'SENT BACK';

/** Badge copy. `NeedsReview` is deliberately accounting's language, not the customer's. */
export const APPROVAL_STATE_LABELS: Record<WorkOrderApprovalState, string> = {
	[WorkOrderApprovalState.NotApplicable]: '',
	[WorkOrderApprovalState.NeedsReview]: 'NEEDS REVIEW',
	[WorkOrderApprovalState.PendingApproval]: 'PENDING APPROVAL',
	[WorkOrderApprovalState.Approved]: 'APPROVED',
};

/** Amber for the state that is waiting on a human; green once it clears. */
export const APPROVAL_STATE_COLORS: Record<WorkOrderApprovalState, string> = {
	[WorkOrderApprovalState.NotApplicable]: 'transparent',
	[WorkOrderApprovalState.NeedsReview]: '#6d4c41',
	[WorkOrderApprovalState.PendingApproval]: '#ef6c00',
	[WorkOrderApprovalState.Approved]: '#2e7d32',
};

/**
 * The one customer-safe string for a work order.
 *
 * Prefers accounting's rewrite and falls back to the raw completion text, matching the
 * `COALESCE(customer_description, work_completed)` the API selects. Exported so a client
 * that already holds a work order doesn't re-invent the precedence and get it backwards.
 */
export function customerFacingDescription(
	wo: { customerDescription?: string | null; workCompleted?: string | null },
): string {
	const reviewed = (wo.customerDescription ?? '').trim();
	if (reviewed) return reviewed;
	return (wo.workCompleted ?? '').trim();
}
